import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { PROMANG_CHECKLIST_TYPE_AI_GUIDE } from "./checklistTypeAiGuide";
import {
  parseImportedChecklistTypeJson,
  SAMPLE_CHECKLIST_TYPE_JSON,
  serializeChecklistTypeForExport,
} from "./checklistTypeImportUtils";

/**
 * View/edit checklist type JSON + copy AI authoring instructions.
 */
export function ChecklistTypeJsonDialog({
  open,
  onClose,
  title = "",
  description = "",
  default_response_actions = [],
  items = [],
  onApply,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [draft, setDraft] = useState("");
  const [importMode, setImportMode] = useState("replace");
  const [parseError, setParseError] = useState("");
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setDraft(
        serializeChecklistTypeForExport({
          title,
          description,
          default_response_actions,
          items,
        })
      );
      setParseError("");
      setTab("json");
      setImportMode("replace");
    }
    prevOpen.current = open;
  }, [open, title, description, default_response_actions, items]);

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      toast.success(t("quality.checklist.type.json.copy_success"));
    } catch {
      toast.error(t("quality.checklist.type.json.clipboard_failed"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await navigator.clipboard.writeText(PROMANG_CHECKLIST_TYPE_AI_GUIDE);
      toast.success(t("quality.checklist.type.json.ai_guide_copied"));
    } catch {
      toast.error(t("quality.checklist.type.json.clipboard_failed"));
    }
  };

  const handleResetDraft = () => {
    setDraft(
      serializeChecklistTypeForExport({
        title,
        description,
        default_response_actions,
        items,
      })
    );
    setParseError("");
    toast.success(t("quality.checklist.type.json.reload_success"));
  };

  const handleInsertSample = () => {
    setDraft(SAMPLE_CHECKLIST_TYPE_JSON);
    setParseError("");
  };

  const handleApply = () => {
    try {
      const parsed = parseImportedChecklistTypeJson(draft);
      onApply?.({ ...parsed, mode: importMode });
      toast.success(
        t("quality.checklist.type.json.applied", { count: parsed.items.length })
      );
      onClose();
    } catch (e) {
      const code = e?.code || "unknown";
      const key = `quality.checklist.type.json.error_${code}`;
      const msg = t(key);
      const display = msg !== key ? msg : e?.message || String(e);
      setParseError(display);
      toast.error(display);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="4xl"
      title={t("quality.checklist.type.json.dialog_title")}
      zIndex={4000}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setTab("json")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "json" ? "bg-sky-100 text-sky-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 shrink-0" />
            {t("quality.checklist.type.json.tab_json")}
          </button>
          <button
            type="button"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "ai" ? "bg-slate-100 text-slate-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BookOpenIcon className="h-5 w-5 shrink-0" />
            {t("quality.checklist.type.json.tab_ai_guide")}
          </button>
        </div>

        {tab === "json" ? (
          <>
            <p className="text-sm text-gray-600">{t("quality.checklist.type.json.hint")}</p>
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setParseError("");
              }}
              spellCheck={false}
              className="min-h-[min(420px,50vh)] w-full flex-1 rounded-lg border border-gray-300 font-mono text-xs leading-relaxed focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
            />
            {parseError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{parseError}</p>
            ) : null}
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="checklistTypeImportMode"
                  checked={importMode === "replace"}
                  onChange={() => setImportMode("replace")}
                />
                {t("quality.checklist.type.json.replace_items")}
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="checklistTypeImportMode"
                  checked={importMode === "append"}
                  onChange={() => setImportMode("append")}
                />
                {t("quality.checklist.type.json.append_items")}
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("quality.checklist.type.json.copy_json")}
              </button>
              <button
                type="button"
                onClick={handleResetDraft}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("quality.checklist.type.json.reload_from_editor")}
              </button>
              <button
                type="button"
                onClick={handleInsertSample}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("quality.checklist.type.json.insert_sample")}
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                {t("quality.checklist.type.json.apply")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {t("quality.checklist.type.json.ai_guide_intro")}
            </p>
            <pre className="max-h-[min(420px,55vh)] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-left font-mono text-xs leading-relaxed text-slate-800">
              {PROMANG_CHECKLIST_TYPE_AI_GUIDE}
            </pre>
            <button
              type="button"
              onClick={handleCopyAiGuide}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t("quality.checklist.type.json.copy_ai_guide")}
            </button>
          </>
        )}
      </div>
    </PMDialog>
  );
}
